import { Box, Button, SimpleGrid, Text } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { getSlaStatus } from './slaUtils'

export type GlanceTile = {
  id: string
  label: string
  value: string
  subValue?: string
  tone?: 'default' | 'green' | 'orange' | 'red' | 'blue'
  onClick?: () => void
}

type Props = {
  tiles: GlanceTile[]
}

const toneMap = {
  default: { border: 'gray.200', value: 'gray.800' },
  green: { border: 'green.200', value: 'green.700' },
  orange: { border: 'orange.200', value: 'orange.700' },
  red: { border: 'red.200', value: 'red.700' },
  blue: { border: 'blue.200', value: 'blue.700' },
}

export function slaTileFromDueAt(slaDueAt: string | null | undefined): Pick<GlanceTile, 'value' | 'subValue' | 'tone'> {
  const sla = getSlaStatus(slaDueAt)
  const tone =
    sla.urgency === 'overdue' ? 'red' : sla.urgency === 'due_soon' ? 'orange' : sla.urgency === 'on_track' ? 'green' : 'default'
  return {
    value: sla.label,
    subValue: sla.detail,
    tone,
  }
}

export default function AtAGlanceStrip({ tiles }: Props) {
  const colors = useThemeColors()

  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
      {tiles.map((tile) => {
        const tone = toneMap[tile.tone ?? 'default']
        const inner = (
          <>
            <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>
              {tile.label}
            </Text>
            <Text fontSize="sm" fontWeight="700" color={tone.value} noOfLines={2}>
              {tile.value}
            </Text>
            {tile.subValue ? (
              <Text fontSize="2xs" color={colors.textMuted} mt={0.5} noOfLines={1}>
                {tile.subValue}
              </Text>
            ) : null}
          </>
        )
        const tileStyles = {
          textAlign: 'left' as const,
          p: 3,
          borderRadius: 'md',
          border: '1px solid',
          borderColor: tone.border,
          bg: colors.cardBg,
          h: 'auto',
          w: '100%',
          whiteSpace: 'normal' as const,
        }
        if (tile.onClick) {
          return (
            <Button
              key={tile.id}
              variant="unstyled"
              onClick={tile.onClick}
              transition="background 0.15s"
              _hover={{ bg: colors.sidebarHoverBg }}
              {...tileStyles}
            >
              {inner}
            </Button>
          )
        }
        return (
          <Box key={tile.id} {...tileStyles}>
            {inner}
          </Box>
        )
      })}
    </SimpleGrid>
  )
}
